// @flow

// Modules
import { filter } from 'lodash';

const EolPattern = /[\r|\n]+/g;
const AssetPathPattern = /(http|file|x-wmapp|\/home).*(client|www|tests)[/\\]+/g;

/**
 * La classe responsable de fournir des fonctionalités de debugging.
 */
const Callstack: Object = {};

/**
 * Génère la callstack à partir du niveau parent.
 *
 * @note Implémentation cross-browser pour ceux ne supportant pas console.trace().
 * @see https://bugsnag.com/blog/js-stacktraces
 * @see http://stackoverflow.com/questions/4671031/print-function-log-stack-trace-for-entire-program-using-firebug
 * @see http://www.eriwen.com/javascript/js-stack-trace
 */
Callstack.getCallstack = function (): Array<string> {
  let callstack: Array<string> = [];
  let isCallstackPopulated: boolean = false;
  let keepLine: boolean = false;

  try {
    // On provoque une exception
    // $FlowFixMe
    i.dont.exist += 0;
  } catch (e) {
    if (e.stack) {
      // Firefox / Chrome
      const lines = e.stack.split('\n');
      const count = lines.length;

      for (let i = 0; i < count; ++i) {
        callstack.push(lines[i].trim());
      }

      isCallstackPopulated = true;
    }
  }

  if (!isCallstackPopulated) {
    // IE / Safari
    let currentFunction = arguments.callee.caller;

    while (currentFunction) {
      const fn = currentFunction.toString();
      const fname = fn.substring(fn.indexOf('function') + 8, fn.indexOf('(')) || 'anonymous';

      callstack.push(fname);
      currentFunction = currentFunction.caller;
    }
  }

  // On ignore tout ce qui est relatif à l'appel de la fonction debug
  callstack = filter(callstack, (line) => {
    if (!keepLine && line.indexOf('getCallstack') !== -1) {
      keepLine = true;
      return false;
    }
    return keepLine;
  });

  return callstack;
};

/**
 * Formate la callstack donnée.
 *
 * @param callstack La callstack sous forme de tableau ou de chaine.
 * @return La callstack formattée sous forme de tableau.
 */
Callstack.formatCallstack = function (callstack: any): ?Array<string> {
  if (!callstack) {
    return null;
  }

  let lines = null;

  if (callstack instanceof Array) {
    lines = callstack;
  } else {
    lines = callstack.split(EolPattern);
  }

  const count = lines && lines.length;

  for (let i = 0; i < count; ++i) {
    let line = lines[i];

    if (line && line.trim) {
      line = line.trim();
      lines[i] = Callstack.formatAssetPath(line);
    }
  }
  return lines;
};

/**
 * Formate un path vers les assets.
 *
 * @param La chaine contenant un path absolu.
 * @return Une nouvelle chaine contenant le path relatif.
 */
Callstack.formatAssetPath = function (s: string): string {
  if (s && s.replace) {
    return s.replace(AssetPathPattern, '').replace('\\', '');
  }
  return s;
};

/**
 * Parse les infos liées à une erreur critique.
 *
 * @param e L'erreur.
 * @param description La description de l'erreur.
 * @param locals Informations sur les variables locales lors de l'erreur.
 * @param file Le chemin vers le fichier source.
 * @param line La ligne dans le fichier source.
 * @param column La colonne dans le fichier source.
 * @return Un objet contenant les informations parsées avec succès.
 */
Callstack.parseError = function (
  e: ?Error,
  description?: ?string,
  locals?: ?Object,
  file?: ?string,
  line?: ?number,
  column?: ?number
): Object {
  const o: Object = {
    description: description || 'Exception'
  };

  if (e && e.stack) {
    // La callstack est connue
    o.callstack = Callstack.formatCallstack(e.stack);
  } else {
    // La callstack n'est pas connue, on la gènère
    o.callstack = Callstack.formatCallstack(Callstack.getCallstack());

    // $FlowFixMe
    if (e && e.toString) {
      // On tente d'obtenir plus d'infos sur l'erreur
      o.error = e.toString();
    }
  }

  if (locals) {
    o.locals = locals;
  }

  if (file) {
    o.file = Callstack.formatAssetPath(file);
  }

  if (line) {
    o.line = line;
  }

  if (column) {
    o.column = column;
  }
  return o;
};

export default Callstack;
